// Valve VPK (v1/v2) reader for the player's own game files. The directory file lists every file;
// contents live in numbered archive files (name_000.vpk...) or in the directory file itself.
// Reads single files by slicing Blobs, so multi-gigabyte archives never get loaded whole.

export interface VpkEntry {
  archive: number;
  offset: number;
  length: number;
  preload: Uint8Array | null;
}

const DIR_ARCHIVE = 0x7fff;

export class VpkError extends Error {}

export class VpkIndex {
  readonly entries = new Map<string, VpkEntry>();
  /** Byte offset of data stored in the directory file itself (archive 0x7fff). */
  readonly dirDataOffset: number;

  constructor(dir: Uint8Array) {
    const v = new DataView(dir.buffer, dir.byteOffset, dir.byteLength);
    if (dir.length < 12 || v.getUint32(0, true) !== 0x55aa1234)
      throw new VpkError('not a VPK directory file');
    const version = v.getUint32(4, true);
    const treeSize = v.getUint32(8, true);
    const headerSize = version === 1 ? 12 : version === 2 ? 28 : -1;
    if (headerSize < 0) throw new VpkError(`VPK version ${version}`);
    this.dirDataOffset = headerSize + treeSize;
    let p = headerSize;
    const end = headerSize + treeSize;
    const str = () => {
      let s = '';
      while (p < end && dir[p] !== 0) s += String.fromCharCode(dir[p++]);
      p++;
      return s;
    };
    for (;;) {
      const ext = str();
      if (!ext) break;
      for (;;) {
        const path = str();
        if (!path) break;
        for (;;) {
          const name = str();
          if (!name) break;
          const preloadBytes = v.getUint16(p + 4, true);
          const archive = v.getUint16(p + 6, true);
          const offset = v.getUint32(p + 8, true);
          const length = v.getUint32(p + 12, true);
          p += 18; // crc, preload, archive, offset, length, terminator
          const preload = preloadBytes ? dir.slice(p, p + preloadBytes) : null;
          p += preloadBytes;
          const full = (path === ' ' ? '' : path + '/') + name + '.' + ext;
          this.entries.set(full.toLowerCase(), { archive, offset, length, preload });
        }
      }
    }
  }

  has(path: string) {
    return this.entries.has(path.toLowerCase());
  }
}

/** A VPK set: directory index plus its archive files, read from Blobs (Files from the player's disk). */
export class VpkArchive {
  constructor(
    readonly index: VpkIndex,
    private readonly dir: Blob,
    private readonly archives: Map<number, Blob>,
  ) {}

  static async open(dir: Blob, archives: Map<number, Blob>): Promise<VpkArchive> {
    // The tree is at the start; read the whole directory file (a few MB at most).
    return new VpkArchive(new VpkIndex(new Uint8Array(await dir.arrayBuffer())), dir, archives);
  }

  has(path: string) {
    return this.index.has(path);
  }

  async read(path: string): Promise<Uint8Array | null> {
    const e = this.index.entries.get(path.toLowerCase());
    if (!e) return null;
    const pre = e.preload ?? new Uint8Array(0);
    if (!e.length) return pre;
    const src = e.archive === DIR_ARCHIVE ? this.dir : this.archives.get(e.archive);
    if (!src) return null;
    const start = e.archive === DIR_ARCHIVE ? this.index.dirDataOffset + e.offset : e.offset;
    const body = new Uint8Array(await src.slice(start, start + e.length).arrayBuffer());
    if (!pre.length) return body;
    const out = new Uint8Array(pre.length + body.length);
    out.set(pre);
    out.set(body, pre.length);
    return out;
  }
}

/** Several VPK sets searched in order (cstrike first, then hl2). */
export class GameFiles {
  constructor(readonly sets: VpkArchive[]) {}

  async read(path: string): Promise<Uint8Array | null> {
    for (const s of this.sets) if (s.has(path)) return s.read(path);
    return null;
  }
}

/** The VPK sets we use, by directory file name. */
export const WANTED_VPKS = ['cstrike_pak', 'hl2_textures', 'hl2_misc', 'hl2_sound_misc'];

/** Group files named like "<set>_dir.vpk" / "<set>_NNN.vpk" into sets we want. */
export function groupVpkFiles(
  files: { name: string; file: Blob }[],
): { name: string; dir: Blob; archives: Map<number, Blob> }[] {
  const out = [];
  for (const set of WANTED_VPKS) {
    const dir = files.find((f) => f.name.toLowerCase() === `${set}_dir.vpk`);
    if (!dir) continue;
    const archives = new Map<number, Blob>();
    for (const f of files) {
      const m = new RegExp(`^${set}_(\\d{3})\\.vpk$`, 'i').exec(f.name);
      if (m) archives.set(parseInt(m[1], 10), f.file);
    }
    out.push({ name: set, dir: dir.file, archives });
  }
  return out;
}
